import { coerceBooleanProperty, coerceNumberProperty } from "@angular/cdk/coercion";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChange,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer, SafeStyle } from "@angular/platform-browser";
import Glide, { GlideEvent, GlideOptions, GlidePattern, GlidePeek } from "@glidejs/glide";
import { coerceBoolean, PlatformService } from "@obg/ngx.global.evo";
import { ElementSize } from "@obg/ngx.ui.global";
import { isEqual, isNumber, uniqueId } from "lodash-es";
import { ReplaySubject, Subscription } from "rxjs";
import { debounceTime, tap } from "rxjs/operators";

import { GlideUtil } from "./glide-sizes.util";
import { GLIDE_CLASSES } from "./glide.const";

@Component({
	selector: "obg-glide",
	templateUrl: "./glide.component.html",
	styleUrls: ["./glide.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class GlideComponent implements OnDestroy, OnChanges, OnInit {
	@Input()
	set arrowsHidden(value: boolean) {
		this._arrowsHidden = coerceBooleanProperty(value);
	}
	get arrowsHidden(): boolean {
		return this._arrowsHidden || !(this.items && this.items.length > 1);
	}

	@Input()
	@coerceBoolean()
	peekHidden = false;

	@Input()
	@coerceBoolean()
	wingsHidden = false;

	@Input()
	set paginationHidden(value: boolean) {
		this._paginationHidden = coerceBooleanProperty(value);
	}
	get paginationHidden(): boolean {
		return this._paginationHidden || !(this.items && this.items.length > 1);
	}

	/**
	 * If set to true, the perView option will be automatically calculated from the available width, slideWidth and the gap option
	 */
	@Input()
	@coerceBoolean()
	autoPerView = false;

	@Input()
	@coerceBoolean()
	useMinSlideWidth = false;

	@Input()
	@coerceBoolean()
	setStaticSlideWidth = false;

	@Input()
	set slideWidth(value: number) {
		this._slideWidth = coerceNumberProperty(value, 1200);
	}
	get slideWidth(): number {
		return this._slideWidth;
	}

	@Input()
	get options(): GlideOptions {
		return this._options;
	}
	set options(value: GlideOptions) {
		this._options = { ...this.defaultOptions, ...value };
	}

	@HostBinding("class.obg-glide") hostCssClass = true;
	@ViewChild("wrapper") wrapper: ElementRef | undefined;

	/**
	 * A reference to the items that the slide will display, the array must match the number of slides in content transclusion
	 * and also note that when this reference changes the slider will re-initialize.
	 */
	@Input() items = [];

	uniqueClass = `obg-glide-wrapper-${uniqueId()}`;
	currentPage = 0;
	leftWingWidth: SafeStyle | number | string = 0;
	rightWingWidth: SafeStyle | number | string = 0;
	glide: Glide | undefined;
	isBrowser: boolean;
	prevArrowHidden = false;
	nextArrowHidden = false;

	private itemChange$ = new ReplaySubject<void>();
	private itemChange$$: Subscription | undefined;
	private defaultOptions: GlideOptions = {
		classes: GLIDE_CLASSES,
	};
	private itemCount: number | undefined;
	private _options: GlideOptions = this.defaultOptions;
	private _arrowsHidden = false;
	private _paginationHidden = false;
	private _slideWidth = 1200;

	constructor(
		private changeDetector: ChangeDetectorRef,
		private sanitizer: DomSanitizer,
		private zone: NgZone,
		private platformService: PlatformService
	) {
		this.isBrowser = this.platformService.isBrowser;
	}

	ngOnInit() {
		this.itemChange$$ = this.platformService
			.forClient(() =>
				this.itemChange$.pipe(
					tap(() => this.destroy()),
					debounceTime(100),
					tap(() => this.initializeGlide())
				)
			)
			.subscribe();
	}

	ngOnChanges(changes: { items?: SimpleChange; slideWidth?: SimpleChange; autoPerView?: SimpleChange }) {
		if (changes.items) {
			this.itemCount = changes.items.currentValue.length;

			if (!isEqual(changes.items.currentValue, changes.items.previousValue)) {
				this.itemChange$.next();
			}
		}

		if ((changes.slideWidth || changes.autoPerView) && this.wrapper) {
			this.resize(this.wrapper.nativeElement.offsetWidth);
		}
	}

	resize(wrapperWidth: number) {
		if (!this.glide) {
			return;
		}

		const peek: GlidePeek | number = this.peekHidden
			? this.options.peek || 0
			: this.getPeekWidth(wrapperWidth, this.slideWidth);
		const updatedOptions: GlideOptions = {
			peek,
			perView: this.getPerView(),
			useMinSlideWidth: this.useMinSlideWidth,
		};

		this.disableArrows(this.glide.index);

		this.zone.runOutsideAngular(() => this.glide!.update(updatedOptions));
		if (!this.wingsHidden) {
			if (isNumber(peek)) {
				this.leftWingWidth = this.rightWingWidth =
					peek > 0 ? this.sanitizer.bypassSecurityTrustStyle(`calc(50% - ${this.slideWidth / 2 - 1}px)`) : 0;
			} else {
				this.leftWingWidth = `${peek.before}px`;
				this.rightWingWidth = `${peek.after}px`;
			}
		}
		this.changeDetector.markForCheck();
	}

	ngOnDestroy() {
		this.destroy();
		if (this.itemChange$$) {
			this.itemChange$$.unsubscribe();
		}
	}

	navigateByPage(page: number) {
		if (!this.glide || this.glide.disabled) {
			return;
		}

		this.zone.runOutsideAngular(() => this.glide!.go(`=${page}`));
	}

	navigateLeft() {
		if (!this.glide || this.glide.disabled) {
			return;
		}

		this.zone.runOutsideAngular(() => this.glide!.go(GlidePattern.backward));
	}

	navigateRight() {
		if (!this.glide || this.glide.disabled) {
			return;
		}

		this.zone.runOutsideAngular(() => this.glide!.go(GlidePattern.forward));
	}

	onWrapperResize(elementSize: ElementSize) {
		this.resize(elementSize.width);
	}

	trackByItems(index: number): number {
		return index;
	}

	update() {
		if (!this.glide) {
			return;
		}

		this.zone.runOutsideAngular(() => this.glide!.update());
		this.changeDetector.markForCheck();
	}

	private destroy() {
		if (!this.glide) {
			return;
		}

		this.zone.runOutsideAngular(() => this.glide!.destroy());
		this.glide = undefined;
		this.currentPage = 0;
	}

	private initializeGlide() {
		const startingOptions = {
			...this.options,
			...(this.setStaticSlideWidth ? { minSlideWidth: this.slideWidth } : {}),
			useMinSlideWidth: this.useMinSlideWidth,
			perView: this.getPerView(),
		};

		// noinspection JSUnusedGlobalSymbols
		this.glide = this.zone.runOutsideAngular(() =>
			new Glide(this.wrapper?.nativeElement ?? `#${this.uniqueClass}`, startingOptions).mount({
				/**
				 * Breakpoints is overridden, as the Breakpoints module from glide listens to window resize
				 * This wrapper can be customized to set breakpoints based on parent width instead
				 */
				Breakpoints: () => ({}),
				/**
				 * Sizes is overridden when the width should be set manually, as the Sizes module from glide doesn't allow it
				 */
				...(this.setStaticSlideWidth ? { Sizes: GlideUtil.Sizes } : {}),
			})
		);

		this.glide.on(GlideEvent.run, () => {
			this.currentPage = this.glide ? this.glide.index : 0;
			this.disableArrows(this.currentPage);
			this.changeDetector.detectChanges();
		});

		this.disableArrows(this.glide.index);

		if (this.wrapper) {
			this.resize(this.wrapper.nativeElement.offsetWidth);
		}

		if (this.items.length <= 1) {
			this.zone.runOutsideAngular(() => this.glide!.disable());
		}
	}

	private getPeekWidth(wrapperWidth: number, slideWidth: number): GlidePeek | number {
		if (this.options.perView === 1 && wrapperWidth > slideWidth) {
			return Math.round((wrapperWidth - slideWidth) / 2);
		}

		return this.options.peek || 0;
	}

	private disableArrows(index: number) {
		if (this.itemCount === undefined || this.options.type === "carousel" || this.options.rewind) {
			return;
		}

		this.prevArrowHidden = index === 0;
		this.nextArrowHidden = index >= this.itemCount - (this.getPerView() || 1);
	}

	private getPerView(): number | undefined {
		return this.wrapper && this.autoPerView
			? this.wrapper.nativeElement.offsetWidth / (this.slideWidth + (this.options.gap || 0))
			: this.options.perView;
	}
}
